import { ChevronDown, ChevronLeft, ClipboardCheck } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { StreamingText } from '@/_shared/components/StreamingText';
import { Button } from '@/_shared/components/ui/button';
import { Textarea } from '@/_shared/components/ui/textarea';
import type { ApprovalPayload, InteractiveAnswer } from '@/_shared/types';

import { approvalAnswer, approvalDismissed } from './answer-helpers';
import { InteractiveCard } from './InteractiveCard';

type PendingAction = 'approve' | 'reject' | 'revise' | null;

interface Props {
  payload: ApprovalPayload;
  onResolve: (answer: InteractiveAnswer) => void;
  submitting?: boolean;
}

/**
 * Simple approval block: markdown description + Reject / Revise… / Approve. Revise swaps the action row
 * for a comment textarea. While an action is in flight (`submitting`) the card is aria-busy and the
 * chosen button shows a spinner. A long description clamps with a fade + "Show full plan". Terminal
 * banners (approved / rejected / revised) render in the transcript via ApprovalResultBanner.
 */
export function ApprovalCard({ payload, onResolve, submitting }: Props): React.ReactElement {
  const [mode, setMode] = useState<'default' | 'revise'>('default');
  const [comment, setComment] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    // Re-measure when the body reflows — a composer width change (artifact panel open/close) or a
    // late web-font swap — so the clamp + "Show full plan" stay in sync instead of measuring once.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [expanded, payload.description]);

  const resolve = (action: Exclude<PendingAction, null>, answer: InteractiveAnswer): void => {
    setPending(action);
    onResolve(answer);
  };

  const busy = Boolean(submitting);
  const spin = (action: PendingAction): boolean => busy && pending === action;

  return (
    <InteractiveCard
      title={payload.title}
      leading={
        <span
          aria-hidden
          className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-foreground"
        >
          <ClipboardCheck className="size-4" />
        </span>
      }
      stats={payload.stats}
      busy={busy}
      ariaLabel={payload.title}
      // Dismiss (✕) denies the ask tool so the agent does not proceed — the escape hatch for an approval
      // (mirrors the question cards); the composer then returns with the normal Stop button available.
      onDismiss={() => onResolve(approvalDismissed(payload.blockId))}
      dismissDisabled={busy}
      footerLeft={
        mode === 'revise' ? (
          <Button variant="secondary" disabled={busy} Icon={<ChevronLeft />} onClick={() => setMode('default')}>
            Back
          </Button>
        ) : (
          <Button
            variant="secondary-destructive"
            disabled={busy}
            loading={spin('reject')}
            onClick={() => resolve('reject', approvalAnswer(payload.blockId, 'rejected'))}
          >
            {payload.rejectLabel ?? 'Reject'}
          </Button>
        )
      }
      footerRight={
        mode === 'revise' ? (
          <Button
            variant="primary"
            loading={spin('revise')}
            disabled={busy}
            onClick={() => resolve('revise', approvalAnswer(payload.blockId, 'revised', comment))}
          >
            Send revision
          </Button>
        ) : (
          <>
            {payload.allowRevise && (
              <Button variant="secondary" disabled={busy} onClick={() => setMode('revise')}>
                {payload.reviseLabel ?? 'Revise…'}
              </Button>
            )}
            <Button
              variant="primary"
              loading={spin('approve')}
              disabled={busy}
              onClick={() => resolve('approve', approvalAnswer(payload.blockId, 'approved'))}
            >
              {payload.approveLabel ?? 'Approve'}
            </Button>
          </>
        )
      }
    >
      <div className="px-2 pt-1 pb-2">
        <div className="relative">
          <div ref={bodyRef} className={expanded ? undefined : 'max-h-40 overflow-hidden'}>
            <StreamingText content={payload.description} />
          </div>
          {overflowing && !expanded && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-linear-to-t from-card to-transparent" />
          )}
        </div>
        {overflowing && !expanded && (
          <Button variant="tertiary" className="mt-1" suffixIcon={<ChevronDown />} onClick={() => setExpanded(true)}>
            Show full plan
          </Button>
        )}

        {mode === 'revise' && (
          <Textarea
            autoFocus
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What should change? (optional)"
            disabled={busy}
            aria-label="Revision comment"
            maxLength={10000}
            className="mt-2 resize-none border-primary shadow-primary"
          />
        )}
      </div>
    </InteractiveCard>
  );
}
